import { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Chip,
  useTheme,
  alpha,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import {
  TuneRounded,
  GradingRounded,
  AppsRounded,
  SaveRounded,
  SchoolRounded,
  PaletteRounded,
  CheckCircleRounded,
  InfoOutlined,
  ImageRounded,
  DeleteRounded,
  PublicRounded,
  UploadFileRounded,
  DnsRounded,
  OpenInNewRounded,
  VerifiedRounded,
  AutoFixHighRounded,
  WarningAmberRounded,
  NotificationsRounded,
  MenuBookRounded,
} from '@mui/icons-material';
import { NotificationsSettingsPage } from '../settings/notifications-settings.page';
import { RubricCatalogEditor, type Rubric as TenantRubricUi } from '../sis/components/RubricEditor';
import { PageEditor } from '../sis/components/PageEditor';

/** Migrate a legacy plain/Markdown doctrinal statement string into a TipTap doc
 *  so the text survives the format change. Blank lines split paragraphs; single
 *  newlines become soft breaks. Markdown marks are NOT parsed — the admin
 *  re-applies formatting with the toolbar. */
function plainTextToTipTap(text: string): Record<string, unknown> {
  const blocks = text
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);
  const paragraphs = blocks.map((block) => {
    const lines = block.split('\n');
    const content: Array<Record<string, unknown>> = [];
    lines.forEach((line, i) => {
      if (i > 0) content.push({ type: 'hardBreak' });
      if (line) content.push({ type: 'text', text: line });
    });
    return { type: 'paragraph', content };
  });
  return {
    type: 'doc',
    content: paragraphs.length ? paragraphs : [{ type: 'paragraph' }],
  };
}
import { doc, getDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useTenantStore } from '../../stores/tenant.store';
import { updateTenantSettings } from '../../lib/api/tenant.api';
import {
  catalogRightsChange,
  buildTenantSettingsPayload,
  settingsTabFromParam,
  SETTINGS_TABS,
  type SettingsTab,
} from './settings-tabs.model';
import { tenantConfigApi } from '../../lib/api/tenant-config.api';
import { storage, db } from '../../lib/firebase';
import type { LandingConfig, StatItem, CustomDomainConfig } from '@dosfilos/domain';
import { errorText } from '../../lib/error-text';
import { orphanedLandingImages } from '../../lib/landing-orphans';
import { validateFeaturedEvent } from '../../lib/featured-events';
import { FeaturedEventsEditor } from './components/FeaturedEventsEditor';
import { FacultyEditor } from './components/FacultyEditor';
import { PillarsEditor } from './components/PillarsEditor';
import { ContactEditor } from './components/ContactEditor';
import { contactErrors, validatePillar } from '../../lib/landing-content';
import { validateFacultyMember } from '../../lib/landing-faculty';
import { todayInZoneWeb } from '@dosfilos/shared';
import { resolverUrl } from '../../lib/environment';
import { callable } from '../../lib/callable';
import { TenantModulesPanel } from '../../components/modules/TenantModulesPanel';
import { GradingScaleEditor } from './components/GradingScaleEditor';
import { StudentCodeEditor } from './components/StudentCodeEditor';
import { BankTransferEditor } from './components/BankTransferEditor';
import { CandidateActivityEditor } from './components/CandidateActivityEditor';
import { CandidateNearEditor } from './components/CandidateNearEditor';

// ─── Preset colour palettes ───────────────────────────────────────────────────

// labelKey resolves to settings.colorPresets.* at render time; value is the stable hex.
const COLOR_PRESETS = [
  { labelKey: 'indigo', value: '#6366f1' },
  { labelKey: 'violet', value: '#8b5cf6' },
  { labelKey: 'emerald', value: '#10b981' },
  { labelKey: 'sky', value: '#0ea5e9' },
  { labelKey: 'amber', value: '#f59e0b' },
  { labelKey: 'pink', value: '#ec4899' },
  { labelKey: 'red', value: '#ef4444' },
  { labelKey: 'slate', value: '#64748b' },
];

const DEFAULT_LANDING: LandingConfig = {
  showStats: true,
  stats: [
    { value: 15, suffix: '+', label: 'Años formando líderes' },
    { value: 500, suffix: '+', label: 'Graduados activos' },
    { value: 3, suffix: '', label: 'Programas académicos' },
    { value: 12, suffix: '', label: 'Países representados' },
  ],
  showPrograms: true,
  ctaAdmissions: true,
  ctaPortal: true,
  ctaFaculty: true,
  featuredEvents: [],
  faculty: [],
  pillars: [],
};

// ─── Tab panel helper ─────────────────────────────────────────────────────────

function TabPanel({
  value,
  index,
  children,
}: {
  value: SettingsTab;
  index: SettingsTab;
  children: React.ReactNode;
}) {
  return value === index ? <Box pt={3}>{children}</Box> : null;
}

const SETTINGS_TAB_ICONS: Record<SettingsTab, React.ReactElement> = {
  identity: <SchoolRounded fontSize="small" />,
  public: <PublicRounded fontSize="small" />,
  academic: <GradingRounded fontSize="small" />,
  framework: <MenuBookRounded fontSize="small" />,
  notifications: <NotificationsRounded fontSize="small" />,
  modules: <AppsRounded fontSize="small" />,
};

// ─── Cloudflare config status ─────────────────────────────────────────────────
type CfStatus = 'idle' | 'configuring' | 'configured' | 'dns_migration_required' | 'error';

// ─── Live preview component ───────────────────────────────────────────────────

function ColorPreview({ color, name }: { color: string; name: string }) {
  const { t } = useTranslation('sis');
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, borderRadius: 2, bgcolor: '#161b22', border: '1px solid #30363d' }}
    >
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        {t('settings.colorPreview.title')}
      </Typography>
      <Stack spacing={2}>
        <Box
          sx={{
            borderRadius: 2,
            p: 2,
            background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <SchoolRounded sx={{ color: 'white', fontSize: 22 }} />
            <Box>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 700 }}>
                {t('settings.colorPreview.studentPortal')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {name || t('settings.colorPreview.seminaryNamePlaceholder')}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            size="small"
            variant="contained"
            sx={{
              bgcolor: color,
              '&:hover': { bgcolor: color, filter: 'brightness(0.9)' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: `0 4px 14px ${color}55`,
            }}
          >
            {t('settings.colorPreview.saveButton')}
          </Button>
          <Chip
            label={t('settings.colorPreview.activeChip')}
            size="small"
            sx={{ bgcolor: `${color}22`, color, fontWeight: 600 }}
          />
        </Stack>
      </Stack>
    </Paper>
  );
}

// ─── Logo Uploader ────────────────────────────────────────────────────────────

function LogoUploader({
  tenantId,
  currentLogoUrl,
  primaryColor,
  onUploaded,
  storageFolderHint = 'logo',
}: {
  tenantId: string;
  currentLogoUrl?: string;
  primaryColor: string;
  onUploaded: (url: string | undefined) => void;
  storageFolderHint?: string;
}) {
  const { t } = useTranslation('sis');
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | undefined>(currentLogoUrl);
  const [error, setError] = useState('');

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.type.match(/^image\/(png|jpeg|webp|svg\+xml)$/)) {
        setError(t('settings.uploader.logo.unsupportedFormat'));
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setError(t('settings.uploader.logo.tooLarge'));
        return;
      }
      setError('');
      setUploading(true);

      const ext = file.name.split('.').pop() ?? 'png';
      const path = `tenants/${tenantId}/assets/${storageFolderHint}.${ext}`;
      try {
        const fileRef2 = storageRef(storage, path);
        await uploadBytes(fileRef2, file, { contentType: file.type });
        const url = await getDownloadURL(fileRef2);
        setPreview(url);
        onUploaded(url);
      } catch (e: unknown) {
        setError(errorText(e, t('settings.uploader.logo.uploadError')));
      } finally {
        setUploading(false);
      }
    },
    [tenantId, onUploaded, storageFolderHint, t],
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <Stack spacing={2.5}>
      {/* Drop zone */}
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragging ? primaryColor : 'divider',
          borderRadius: 3,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: dragging ? alpha(primaryColor, 0.05) : 'action.hover',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: primaryColor,
            bgcolor: alpha(primaryColor, 0.05),
          },
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          style={{ display: 'none' }}
          onChange={onInputChange}
        />
        {uploading ? (
          <Stack alignItems="center" spacing={1}>
            <CircularProgress size={32} sx={{ color: primaryColor }} />
            <Typography variant="body2" color="text.secondary">
              {t('settings.uploader.logo.uploading')}
            </Typography>
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={1}>
            <UploadFileRounded sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography variant="body2" fontWeight={600}>
              {t('settings.uploader.logo.dropHint')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('settings.uploader.logo.formatsHint')}
            </Typography>
          </Stack>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Preview */}
      {preview && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            {t('settings.uploader.logo.previewTitle')}
          </Typography>
          {/* Dark background */}
          <Stack direction="row" gap={3} flexWrap="wrap">
            <Box
              sx={{
                bgcolor: '#0d1117',
                borderRadius: 2,
                p: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flex: 1,
                minWidth: 200,
              }}
            >
              <Box
                component="img"
                src={preview}
                alt={t('settings.uploader.logo.darkPreviewAlt')}
                sx={{ height: 48, maxWidth: 180, objectFit: 'contain' }}
                onError={() => setPreview(undefined)}
              />
              <Typography fontWeight={700} color="white" noWrap>
                {t('settings.uploader.logo.darkBg')}
              </Typography>
            </Box>
            {/* Light background */}
            <Box
              sx={{
                bgcolor: '#f8fafc',
                borderRadius: 2,
                p: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flex: 1,
                minWidth: 200,
              }}
            >
              <Box
                component="img"
                src={preview}
                alt={t('settings.uploader.logo.lightPreviewAlt')}
                sx={{ height: 48, maxWidth: 180, objectFit: 'contain' }}
              />
              <Typography fontWeight={700} color="#0d1117" noWrap>
                {t('settings.uploader.logo.lightBg')}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" mt={2}>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteRounded />}
              onClick={() => {
                setPreview(undefined);
                onUploaded('');
              }}
            >
              {t('settings.uploader.logo.remove')}
            </Button>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

// ─── Hero Image Uploader ─────────────────────────────────────────────────────

function HeroImageUploader({
  tenantId,
  currentImageUrl,
  primaryColor,
  onUploaded,
}: {
  tenantId: string;
  currentImageUrl?: string;
  primaryColor: string;
  onUploaded: (url: string | undefined) => void;
}) {
  const { t } = useTranslation('sis');
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | undefined>(currentImageUrl);
  const [error, setError] = useState('');

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.type.match(/^image\/(png|jpeg|webp|svg\+xml)$/)) {
        setError(t('settings.uploader.hero.unsupportedFormat'));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError(t('settings.uploader.hero.tooLarge'));
        return;
      }
      setError('');
      setUploading(true);
      const ext = file.name.split('.').pop() ?? 'jpg';
      const path = `tenants/${tenantId}/assets/hero-bg.${ext}`;
      try {
        const ref = storageRef(storage, path);
        await uploadBytes(ref, file, { contentType: file.type });
        const url = await getDownloadURL(ref);
        setPreview(url);
        onUploaded(url);
      } catch (e: unknown) {
        setError(errorText(e, t('settings.uploader.hero.uploadError')));
      } finally {
        setUploading(false);
      }
    },
    [tenantId, onUploaded, t],
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <Stack spacing={2}>
      {/* Drop zone */}
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragging ? primaryColor : 'divider',
          borderRadius: 3,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: dragging ? alpha(primaryColor, 0.05) : 'action.hover',
          transition: 'all 0.2s ease',
          '&:hover': { borderColor: primaryColor, bgcolor: alpha(primaryColor, 0.05) },
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
        {uploading ? (
          <Stack alignItems="center" spacing={1}>
            <CircularProgress size={28} sx={{ color: primaryColor }} />
            <Typography variant="body2" color="text.secondary">
              {t('settings.uploader.hero.uploading')}
            </Typography>
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={0.5}>
            <ImageRounded sx={{ fontSize: 36, color: 'text.disabled' }} />
            <Typography variant="body2" fontWeight={600}>
              {t('settings.uploader.hero.dropHint')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('settings.uploader.hero.formatsHint')}
            </Typography>
          </Stack>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Live preview — shows how the image looks diluted on the dark hero */}
      {preview && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ position: 'relative', height: 160, bgcolor: '#080a0a' }}>
            <Box
              component="img"
              src={preview}
              alt={t('settings.uploader.hero.previewAlt')}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center 30%',
                opacity: 0.18,
                filter: 'grayscale(30%) brightness(0.7)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(to bottom, rgba(8,10,10,0.33) 0%, transparent 40%, rgba(8,10,10,0.85) 100%)',
              }}
            />
            <Stack sx={{ position: 'absolute', bottom: 16, left: 20 }} spacing={0.5}>
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontFamily: 'Georgia, serif',
                  fontWeight: 400,
                  fontSize: 20,
                  lineHeight: 1.1,
                }}
              >
                {t('settings.uploader.hero.previewName')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                {t('settings.uploader.hero.previewCaption')}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ p: 1.5 }}>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteRounded />}
              onClick={() => {
                setPreview(undefined);
                onUploaded(undefined);
              }}
            >
              {t('settings.uploader.hero.remove')}
            </Button>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

// Query-param (?tab=) → tab index, so the Setup Center can deep-link a tab.

// Curated IANA zones offered in the tenant localization selector. The value is the
// canonical zone id (stored as-is); the label is a friendly city + region. Covers the
// countries in `timezoneForCountry`; a tenant elsewhere can be handled case-by-case.
const TZ_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/Santiago', label: 'Chile — Santiago' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina — Buenos Aires' },
  { value: 'America/Lima', label: 'Perú — Lima' },
  { value: 'America/Bogota', label: 'Colombia — Bogotá' },
  { value: 'America/Mexico_City', label: 'México — Ciudad de México' },
  { value: 'America/Guayaquil', label: 'Ecuador — Guayaquil' },
  { value: 'America/La_Paz', label: 'Bolivia — La Paz' },
  { value: 'America/Asuncion', label: 'Paraguay — Asunción' },
  { value: 'America/Montevideo', label: 'Uruguay — Montevideo' },
  { value: 'America/Caracas', label: 'Venezuela — Caracas' },
  { value: 'America/Sao_Paulo', label: 'Brasil — São Paulo' },
  { value: 'America/Costa_Rica', label: 'Costa Rica' },
  { value: 'America/Panama', label: 'Panamá' },
  { value: 'America/Guatemala', label: 'Guatemala' },
  { value: 'America/Tegucigalpa', label: 'Honduras' },
  { value: 'America/El_Salvador', label: 'El Salvador' },
  { value: 'America/Managua', label: 'Nicaragua' },
  { value: 'America/Santo_Domingo', label: 'Rep. Dominicana' },
  { value: 'America/Puerto_Rico', label: 'Puerto Rico' },
  { value: 'America/New_York', label: 'EE.UU. — Nueva York (Este)' },
  { value: 'America/Chicago', label: 'EE.UU. — Chicago (Central)' },
  { value: 'America/Denver', label: 'EE.UU. — Denver (Montaña)' },
  { value: 'America/Los_Angeles', label: 'EE.UU. — Los Ángeles (Pacífico)' },
  { value: 'America/Toronto', label: 'Canadá — Toronto' },
  { value: 'Europe/Madrid', label: 'España — Madrid' },
  { value: 'Europe/Lisbon', label: 'Portugal — Lisboa' },
  { value: 'Europe/London', label: 'Reino Unido — Londres' },
  { value: 'Europe/Paris', label: 'Francia — París' },
  { value: 'Europe/Berlin', label: 'Alemania — Berlín' },
  { value: 'Europe/Rome', label: 'Italia — Roma' },
];

// Numeric date-ordering options. The example shows how 31 Dec 2026 renders.
const DATE_FORMAT_OPTIONS: { value: string; example: string }[] = [
  { value: 'dd-mm-yyyy', example: '31-12-2026' },
  { value: 'mm-dd-yyyy', example: '12-31-2026' },
  { value: 'yyyy-mm-dd', example: '2026-12-31' },
];

// Clock convention options. The example shows how 14:30 renders.
const TIME_FORMAT_OPTIONS: { value: string; example: string }[] = [
  { value: '24h', example: '14:30' },
  { value: '12h', example: '2:30 PM' },
];

export function TenantSettingsPage() {
  const { t } = useTranslation('sis');
  const theme = useTheme();
  const {
    currentTenantId,
    availableTenants,
    currentTenantTheme,
    setTenantTheme,
    setAvailableTenants,
  } = useTenantStore();

  const currentTenant = availableTenants.find((t) => t.id === currentTenantId);

  // La pestaña vive en la URL (?tab=modules): la ayuda y el asistente enlazan
  // directo. Una vieja (?tab=logo) se traduce; una desconocida cae en la primera.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = settingsTabFromParam(searchParams.get('tab'));
  const setTab = (next: SettingsTab) =>
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('tab', next);
        return p;
      },
      { replace: true },
    );

  // ── Identity fields ──
  const [name, setName] = useState(currentTenant?.name ?? '');
  const [slug, setSlug] = useState(currentTenant?.slug ?? '');
  const [primaryColor, setPrimaryColor] = useState(
    currentTenantTheme?.primaryColor ?? theme.palette.primary.main,
  );

  // ── Logo field ──
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  const [documentLogoUrl, setDocumentLogoUrl] = useState<string | undefined>(undefined);

  // ── Localization ──
  const [timezone, setTimezone] = useState('');
  const [catalogRightsExpiryYears, setCatalogRightsExpiryYears] = useState<number | ''>('');
  // Lo que está GUARDADO, para saber si «Guardar» tiene que mandarlo.
  const [savedCatalogRightsYears, setSavedCatalogRightsYears] = useState<number | null>(null);
  const [dateFormat, setDateFormat] = useState('');
  const [timeFormat, setTimeFormat] = useState('');

  // ── Institutional mission ──
  const [missionText, setMissionText] = useState('');
  const [missionScripture, setMissionScripture] = useState('');

  // ── CAP: TMAI site abbreviation (Appendix A field e) ──
  const [tmaiSiteAbbreviation, setTmaiSiteAbbreviation] = useState('');
  // ── TMAI: doctrinal statement (TipTap JSON) shown when signing the attestation ──
  const [doctrinalStatement, setDoctrinalStatement] = useState<Record<string, unknown> | null>(
    null,
  );

  // ── Institutional rubrics catalog (Phase C.1) ──
  const [tenantRubrics, setTenantRubrics] = useState<TenantRubricUi[]>([]);

  // ── Landing fields ──
  const [landing, setLanding] = useState<LandingConfig>({ ...DEFAULT_LANDING });
  // Lo último que se leyó o guardó del landing: contra eso se calculan las
  // imágenes que el guardado dejó sin uso (y que se borran después de guardar).
  const savedLandingRef = useRef<Partial<LandingConfig> | null>(null);
  const patchLanding = (patch: Partial<LandingConfig>) =>
    setLanding((prev: LandingConfig) => ({ ...prev, ...patch }));

  // ── Custom Domain fields ──
  const [customDomains, setCustomDomains] = useState<CustomDomainConfig>({});
  const [savedCustomDomains, setSavedCustomDomains] = useState<CustomDomainConfig>({});
  const [domainVerifying, setDomainVerifying] = useState(false);
  const [domainVerifyResult, setDomainVerifyResult] = useState<{
    landing?: boolean;
    backoffice?: boolean;
  }>({});

  // ── Cloudflare auto-config state ──
  const [cfStatus, setCfStatus] = useState<CfStatus>('idle');
  const [cfMessage, setCfMessage] = useState<string | undefined>(undefined);

  // ── Data loading from Firestore ──
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    if (!currentTenantId) return;
    let cancelled = false;
    setConfigLoading(true);
    getDoc(doc(db, 'tenants', currentTenantId))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as Record<string, unknown>;
        const cfg = (data.config ?? {}) as Record<string, unknown>;
        const savedLanding = (cfg.landing ?? {}) as Partial<LandingConfig>;

        // Populate landing config
        setLanding({ ...DEFAULT_LANDING, ...savedLanding });
        savedLandingRef.current = savedLanding;

        // Populate logo url
        if (cfg.logoUrl !== undefined) {
          setLogoUrl(cfg.logoUrl as string);
        }
        if (cfg.documentLogoUrl !== undefined) {
          setDocumentLogoUrl(cfg.documentLogoUrl as string);
        }

        // Populate the tenant zone + date format (localization)
        if (cfg.timezone) {
          setTimezone(cfg.timezone as string);
        }
        setCatalogRightsExpiryYears(
          typeof cfg.catalogRightsExpiryYears === 'number' ? cfg.catalogRightsExpiryYears : '',
        );
        setSavedCatalogRightsYears(
          typeof cfg.catalogRightsExpiryYears === 'number' ? cfg.catalogRightsExpiryYears : null,
        );
        if (cfg.dateFormat) {
          setDateFormat(cfg.dateFormat as string);
        }
        if (cfg.timeFormat) {
          setTimeFormat(cfg.timeFormat as string);
        }
        if (cfg.tmaiSiteAbbreviation) {
          setTmaiSiteAbbreviation(cfg.tmaiSiteAbbreviation as string);
        }
        // New rich-text JSON loads as-is; a legacy Markdown/plain string is
        // migrated to a TipTap doc so the text is preserved (formatting is
        // re-applied with the toolbar and persisted on the next save).
        if (cfg.doctrinalStatement) {
          const ds = cfg.doctrinalStatement;
          if (typeof ds === 'object') {
            setDoctrinalStatement(ds as Record<string, unknown>);
          } else if (typeof ds === 'string' && ds.trim()) {
            setDoctrinalStatement(plainTextToTipTap(ds));
          }
        }

        // Populate institutional mission
        if (cfg.mission) {
          const m = cfg.mission as { text?: string; scripture?: string };
          setMissionText(m.text ?? '');
          setMissionScripture(m.scripture ?? '');
        }

        // Populate institutional rubrics (Phase C.1).
        // Backcompat: legacy single researchRubric promotes to rubrics[0].
        const cfgRubrics = (cfg.rubrics as TenantRubricUi[] | undefined) ?? null;
        if (cfgRubrics && Array.isArray(cfgRubrics) && cfgRubrics.length > 0) {
          setTenantRubrics(cfgRubrics);
        } else if (cfg.researchRubric) {
          setTenantRubrics([cfg.researchRubric as TenantRubricUi]);
        }

        // Populate color if not already in store
        if (cfg.primaryColor && !currentTenantTheme?.primaryColor) {
          setPrimaryColor(cfg.primaryColor as string);
        }

        // Populate custom domains
        if (cfg.customDomains) {
          const loaded = cfg.customDomains as CustomDomainConfig;
          setCustomDomains(loaded);
          setSavedCustomDomains(loaded);
        }
      })
      .catch(console.error)
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId]);

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const handleSlugChange = (raw: string) => {
    setSlug(raw.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  };

  const isValid =
    name.trim().length > 0 &&
    /^[a-z0-9-]+$/.test(slug) &&
    (landing.featuredEvents ?? []).every((e) => validateFeaturedEvent(e).length === 0) &&
    (landing.faculty ?? []).every((m) => validateFacultyMember(m).length === 0) &&
    (landing.pillars ?? []).every((p) => validatePillar(p).length === 0) &&
    contactErrors(landing.contact).length === 0;

  const handleVerifyDns = useCallback(async () => {
    setDomainVerifying(true);
    setDomainVerifyResult({});
    const results: { landing?: boolean; backoffice?: boolean } = {};

    // Simple DNS check: try to fetch the resolve endpoint for each domain
    const resolverBase = resolverUrl();

    const check = async (domain: string | undefined): Promise<boolean> => {
      if (!domain) return false;
      try {
        const res = await fetch(`${resolverBase}?domain=${encodeURIComponent(domain)}`);
        const json = (await res.json()) as { slug?: string };
        return json.slug === slug;
      } catch {
        return false;
      }
    };

    results.landing = await check(customDomains.landing);
    results.backoffice = await check(customDomains.backoffice);
    setDomainVerifyResult(results);
    setDomainVerifying(false);
  }, [customDomains, slug]);

  const handleSave = useCallback(async () => {
    if (!currentTenantId || !isValid) return;
    setSaving(true);
    // Track previous domains to allow KV cache invalidation
    const previousLandingDomain = savedCustomDomains.landing;
    const previousBackofficeDomain = savedCustomDomains.backoffice;
    try {
      await updateTenantSettings(
        buildTenantSettingsPayload({
          tenantId: currentTenantId,
          name,
          slug,
          primaryColor,
          logoUrl,
          documentLogoUrl,
          missionText,
          missionScripture,
          timezone,
          dateFormat,
          timeFormat,
          tmaiSiteAbbreviation,
          catalogRightsExpiryYears,
          doctrinalStatement,
          landing,
          customDomains,
          rubrics: tenantRubrics,
        }),
      );

      // La vigencia de los derechos de catálogo va por el callable (validada y
      // auditada): las reglas no dejan escribirla desde el navegador.
      const catalogYears = catalogRightsChange(catalogRightsExpiryYears, savedCatalogRightsYears);
      if (catalogYears !== undefined) {
        await tenantConfigApi.updateTenantConfig({
          tenantId: currentTenantId,
          catalogRightsExpiryYears: catalogYears,
        });
        setSavedCatalogRightsYears(catalogYears);
      }

      setTenantTheme({ primaryColor, name, slug, ...(logoUrl ? { logoUrl } : {}) });
      setAvailableTenants(
        availableTenants.map((t) => (t.id === currentTenantId ? { ...t, name, slug } : t)),
      );

      setToast({ msg: t('settings.common.saveSuccess'), severity: 'success' });

      // Imágenes de eventos y docentes que este guardado dejó sin uso: se borran
      // ahora, no al hacer clic en «Quitar» (si no se guardaba, el landing
      // quedaba apuntando a un archivo inexistente). Un borrado que falla no
      // molesta al director: el archivo solo queda huérfano, como antes.
      const orphans = orphanedLandingImages(savedLandingRef.current, landing, currentTenantId);
      savedLandingRef.current = landing;
      if (orphans.length > 0) {
        void Promise.allSettled(orphans.map((u) => deleteObject(storageRef(storage, u)))).then(
          (results) =>
            results.forEach((r, i) => {
              if (r.status === 'rejected') {
                console.warn(
                  '[TenantSettings] no se pudo borrar una imagen sin uso',
                  orphans[i],
                  r.reason,
                );
              }
            }),
        );
      }

      // ── Auto-configure Cloudflare if domains are set ──
      if (customDomains.landing || customDomains.backoffice) {
        setCfStatus('configuring');
        setCfMessage(undefined);
        try {
          const configureDomain = callable<unknown, { status: string; message?: string }>(
            'configureDomain',
          );
          const result = await configureDomain({
            tenantId: currentTenantId,
            landingDomain: customDomains.landing,
            backofficeDomain: customDomains.backoffice,
            previousLandingDomain,
            previousBackofficeDomain,
          });
          const { status, message } = result.data;
          setCfStatus(status as CfStatus);
          setCfMessage(message);
          if (status === 'configured') {
            setSavedCustomDomains(customDomains);
          }
        } catch (cfErr: unknown) {
          setCfStatus('error');
          setCfMessage(errorText(cfErr, t('settings.domain.cfConfigError')));
        }
      }
    } catch (e: unknown) {
      setToast({ msg: (e as Error)?.message ?? t('settings.common.saveError'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [
    currentTenantId,
    savedCustomDomains,
    name,
    slug,
    primaryColor,
    logoUrl,
    documentLogoUrl,
    timezone,
    dateFormat,
    timeFormat,
    missionText,
    missionScripture,
    tmaiSiteAbbreviation,
    // Faltaba: sin ella, «Guardar» podía mandar una vigencia de catálogo vieja.
    catalogRightsExpiryYears,
    doctrinalStatement,
    tenantRubrics,
    landing,
    customDomains,
    isValid,
    availableTenants,
    setTenantTheme,
    setAvailableTenants,
    t,
  ]);

  if (!currentTenantId) {
    return (
      <Box p={4} textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  const landingUrl = `${window.location.origin}/s/${slug}`;

  return (
    <Box maxWidth={1100} mx="auto">
      {/* Page header */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
        <TuneRounded color="primary" />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {t('settings.header.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('settings.header.subtitle')}
          </Typography>
        </Box>
      </Stack>

      {/* Landing URL badge */}
      {slug && (
        <Paper
          variant="outlined"
          sx={{
            px: 2,
            py: 1.5,
            mb: 3,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <PublicRounded fontSize="small" sx={{ color: 'success.main' }} />
          <Typography variant="body2" color="text.secondary">
            {t('settings.landingBadge.label')}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            component="a"
            href={landingUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {landingUrl}
          </Typography>
        </Paper>
      )}

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v: SettingsTab) => setTab(v)}
            textColor="primary"
            indicatorColor="primary"
            // Seis pestañas que caben en 1280 px; en pantallas angostas, flechas.
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ '& .MuiTab-root': { textTransform: 'none', minHeight: 56 } }}
          >
            {SETTINGS_TABS.map((key) => (
              <Tab
                key={key}
                value={key}
                icon={SETTINGS_TAB_ICONS[key]}
                iconPosition="start"
                label={t(`settings.tabs.${key}`)}
              />
            ))}
          </Tabs>
        </Box>

        <CardContent sx={{ p: 3 }}>
          {configLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              {/* ── Identidad: nombre, slug, color, logos y localización ── */}
              <TabPanel value={tab} index="identity">
                <Stack spacing={3}>
                  <Stack spacing={2.5}>
                    <TextField
                      label={t('settings.identity.nameLabel')}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      fullWidth
                      helperText={t('settings.identity.nameHelper')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SchoolRounded fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      label={t('settings.identity.slugLabel')}
                      value={slug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      fullWidth
                      helperText={t('settings.identity.slugHelper')}
                      error={slug.length > 0 && !/^[a-z0-9-]+$/.test(slug)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="body2" color="text.secondary">
                              /s/
                            </Typography>
                          </InputAdornment>
                        ),
                        endAdornment:
                          slug.length > 0 && /^[a-z0-9-]+$/.test(slug) ? (
                            <InputAdornment position="end">
                              <CheckCircleRounded fontSize="small" color="success" />
                            </InputAdornment>
                          ) : null,
                      }}
                    />
                  </Stack>

                  <Divider />

                  {/* Color theme */}
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <PaletteRounded fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t('settings.identity.themeColor')}
                    </Typography>
                    <Tooltip title={t('settings.identity.themeColorTooltip')}>
                      <InfoOutlined
                        fontSize="small"
                        sx={{ color: 'text.disabled', cursor: 'help' }}
                      />
                    </Tooltip>
                  </Stack>

                  <Stack direction="row" flexWrap="wrap" gap={1.5} mb={2}>
                    {COLOR_PRESETS.map((preset) => (
                      <Tooltip
                        key={preset.value}
                        title={t(`settings.colorPresets.${preset.labelKey}`)}
                      >
                        <Box
                          onClick={() => setPrimaryColor(preset.value)}
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: preset.value,
                            cursor: 'pointer',
                            border:
                              primaryColor === preset.value
                                ? `3px solid white`
                                : '3px solid transparent',
                            boxShadow:
                              primaryColor === preset.value ? `0 0 0 2px ${preset.value}` : 'none',
                            transition: 'transform 0.15s, box-shadow 0.15s',
                            '&:hover': { transform: 'scale(1.15)' },
                          }}
                        />
                      </Tooltip>
                    ))}

                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: primaryColor,
                          border: '3px solid',
                          borderColor: 'divider',
                          flexShrink: 0,
                        }}
                      />
                      <TextField
                        size="small"
                        value={primaryColor}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^#[0-9a-fA-F]{0,6}$/.test(val)) setPrimaryColor(val);
                        }}
                        sx={{ width: 110 }}
                        inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">#</InputAdornment>,
                        }}
                        label={t('settings.identity.hexLabel')}
                      />
                    </Stack>
                  </Stack>

                  <ColorPreview color={primaryColor} name={name} />
                </Stack>

                <Divider sx={{ my: 4 }} />

                <Stack spacing={4}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                      {t('settings.logo.mainTitle')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('settings.logo.mainDesc')}
                    </Typography>
                  </Box>
                  <LogoUploader
                    tenantId={currentTenantId}
                    {...(logoUrl !== undefined ? { currentLogoUrl: logoUrl } : {})}
                    primaryColor={primaryColor}
                    onUploaded={setLogoUrl}
                  />

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                      {t('settings.logo.documentTitle')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('settings.logo.documentDesc')}
                    </Typography>
                  </Box>
                  <LogoUploader
                    tenantId={currentTenantId}
                    storageFolderHint="document-logo"
                    {...(documentLogoUrl !== undefined ? { currentLogoUrl: documentLogoUrl } : {})}
                    primaryColor={primaryColor}
                    onUploaded={setDocumentLogoUrl}
                  />
                </Stack>

                <Divider sx={{ my: 4 }} />

                <Stack spacing={3} sx={{ maxWidth: 1000 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                      {t('settings.localization.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('settings.localization.desc')}
                    </Typography>
                  </Box>
                  <TextField
                    select
                    label={t('settings.localization.timezoneLabel')}
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    size="small"
                    helperText={t('settings.localization.timezoneHelper')}
                    sx={{ maxWidth: 420 }}
                  >
                    {timezone && !TZ_OPTIONS.some((o) => o.value === timezone) && (
                      <MenuItem value={timezone}>{timezone}</MenuItem>
                    )}
                    {TZ_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t('settings.localization.dateFormatLabel')}
                    value={dateFormat || 'dd-mm-yyyy'}
                    onChange={(e) => setDateFormat(e.target.value)}
                    size="small"
                    helperText={t('settings.localization.dateFormatHelper')}
                    sx={{ maxWidth: 420 }}
                  >
                    {DATE_FORMAT_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {`${o.value} — ${o.example}`}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t('settings.localization.timeFormatLabel')}
                    value={timeFormat || '24h'}
                    onChange={(e) => setTimeFormat(e.target.value)}
                    size="small"
                    helperText={t('settings.localization.timeFormatHelper')}
                    sx={{ maxWidth: 420 }}
                  >
                    {TIME_FORMAT_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {`${t(`settings.localization.timeFormatOption.${o.value}`)} — ${o.example}`}
                      </MenuItem>
                    ))}
                  </TextField>
                  {/* SEC-3: los datos de transferencia que ve el alumno al pagar. */}
                  {currentTenantId && <BankTransferEditor tenantId={currentTenantId} />}
                </Stack>
              </TabPanel>

              {/* ── Página pública: landing y dominio ───────────────── */}
              <TabPanel value={tab} index="public">
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                      {t('settings.landing.contentTitle')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('settings.landing.contentDesc_prefix')}
                      <strong>/s/{slug || t('settings.landing.slugPlaceholder')}</strong>
                      {t('settings.landing.contentDesc_suffix')}
                    </Typography>
                  </Box>

                  {/* Hero section */}
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      display="block"
                      mb={2}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      {t('settings.landing.heroSection')}
                    </Typography>
                    <Stack spacing={2}>
                      <TextField
                        label={t('settings.landing.heroTitleLabel')}
                        placeholder={t('settings.landing.heroTitlePlaceholder')}
                        value={landing.heroTitle ?? ''}
                        onChange={(e) => patchLanding({ heroTitle: e.target.value })}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label={t('settings.landing.heroSubtitleLabel')}
                        placeholder={t('settings.landing.heroSubtitlePlaceholder')}
                        value={landing.heroSubtitle ?? ''}
                        onChange={(e) => patchLanding({ heroSubtitle: e.target.value })}
                        fullWidth
                        size="small"
                      />

                      <Divider />

                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                          <ImageRounded fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={600}>
                            {t('settings.landing.heroImageLabel')}
                          </Typography>
                          <Tooltip title={t('settings.landing.heroImageTooltip')}>
                            <InfoOutlined
                              fontSize="small"
                              sx={{ color: 'text.disabled', cursor: 'help' }}
                            />
                          </Tooltip>
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={1.5}
                        >
                          {t('settings.landing.heroImageHelper')}
                        </Typography>
                        <HeroImageUploader
                          tenantId={currentTenantId}
                          {...(landing.heroImageUrl
                            ? { currentImageUrl: landing.heroImageUrl }
                            : {})}
                          primaryColor={primaryColor}
                          onUploaded={(url) => patchLanding({ heroImageUrl: url ?? '' })}
                        />
                      </Box>
                    </Stack>
                  </Paper>

                  {/* Featured events — primer plano del landing */}
                  <FeaturedEventsEditor
                    tenantId={currentTenantId}
                    events={landing.featuredEvents ?? []}
                    today={todayInZoneWeb(timezone || 'America/Santiago')}
                    onChange={(featuredEvents) => patchLanding({ featuredEvents })}
                  />

                  {/* Cuerpo académico */}
                  <FacultyEditor
                    tenantId={currentTenantId}
                    members={landing.faculty ?? []}
                    intro={landing.facultyIntro ?? ''}
                    onChange={(faculty) => patchLanding({ faculty })}
                    onIntroChange={(facultyIntro) => patchLanding({ facultyIntro })}
                  />

                  {/* Pilares (los escribe el seminario) */}
                  <PillarsEditor
                    pillars={landing.pillars ?? []}
                    onChange={(pillars) => patchLanding({ pillars })}
                  />

                  {/* About section */}
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      display="block"
                      mb={2}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      {t('settings.landing.aboutSection')}
                    </Typography>
                    <TextField
                      label={t('settings.landing.aboutLabel')}
                      placeholder={t('settings.landing.aboutPlaceholder')}
                      value={landing.about ?? ''}
                      onChange={(e) => patchLanding({ about: e.target.value })}
                      fullWidth
                      multiline
                      rows={4}
                      size="small"
                    />
                  </Paper>

                  {/* Stats section */}
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      display="block"
                      mb={2}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      {t('settings.landing.statsSection')}
                    </Typography>
                    <Stack spacing={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={landing.showStats ?? true}
                            onChange={(e) => patchLanding({ showStats: e.target.checked })}
                            color="primary"
                          />
                        }
                        label={
                          <Stack>
                            <Typography variant="body2" fontWeight={600}>
                              {t('settings.landing.statsToggleLabel')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('settings.landing.statsToggleDesc')}
                            </Typography>
                          </Stack>
                        }
                        sx={{ alignItems: 'flex-start', py: 0.5 }}
                      />

                      {(landing.showStats ?? true) && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={1.5}
                          >
                            {t('settings.landing.statsHelper_prefix')}
                            <strong>+</strong>
                            {t('settings.landing.statsHelper_mid')}
                            <strong>%</strong>.
                          </Typography>
                          <Stack spacing={1.5}>
                            {(landing.stats ?? DEFAULT_LANDING.stats).map((stat, idx) => (
                              <Paper
                                key={idx}
                                variant="outlined"
                                sx={{ p: 1.5, borderRadius: 1.5 }}
                              >
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <TextField
                                    select
                                    label={t('settings.landing.statsSourceLabel')}
                                    value={stat.source ?? 'manual'}
                                    onChange={(e) => {
                                      const next = [...(landing.stats ?? DEFAULT_LANDING.stats)];
                                      // Sin `source` = valor escrito. Se quita la clave en vez de
                                      // dejarla en undefined: Firestore no acepta undefined.
                                      const { source: _prev, ...rest } = next[idx] as StatItem;
                                      next[idx] =
                                        e.target.value === 'activePrograms'
                                          ? { ...rest, source: 'activePrograms' }
                                          : rest;
                                      patchLanding({ stats: next });
                                    }}
                                    size="small"
                                    sx={{ width: 170, flexShrink: 0 }}
                                  >
                                    <MenuItem value="manual">
                                      {t('settings.landing.statsSourceManual')}
                                    </MenuItem>
                                    <MenuItem value="activePrograms">
                                      {t('settings.landing.statsSourceActivePrograms')}
                                    </MenuItem>
                                  </TextField>
                                  <TextField
                                    label={t('settings.landing.statsValueLabel')}
                                    type="number"
                                    disabled={stat.source === 'activePrograms'}
                                    {...(stat.source === 'activePrograms'
                                      ? { title: t('settings.landing.statsSourceAutoHint') }
                                      : {})}
                                    value={stat.value}
                                    onChange={(e) => {
                                      const next = [...(landing.stats ?? DEFAULT_LANDING.stats)];
                                      next[idx] = {
                                        ...next[idx],
                                        value: Number(e.target.value),
                                      } as StatItem;
                                      patchLanding({ stats: next });
                                    }}
                                    size="small"
                                    sx={{ width: 90 }}
                                    inputProps={{ min: 0 }}
                                  />
                                  <TextField
                                    label={t('settings.landing.statsSuffixLabel')}
                                    value={stat.suffix}
                                    onChange={(e) => {
                                      const next = [...(landing.stats ?? DEFAULT_LANDING.stats)];
                                      next[idx] = {
                                        ...next[idx],
                                        suffix: e.target.value,
                                      } as StatItem;
                                      patchLanding({ stats: next });
                                    }}
                                    size="small"
                                    sx={{ width: 72 }}
                                    placeholder="+"
                                  />
                                  <TextField
                                    label={t('settings.landing.statsLabelLabel')}
                                    value={stat.label}
                                    onChange={(e) => {
                                      const next = [...(landing.stats ?? DEFAULT_LANDING.stats)];
                                      next[idx] = {
                                        ...next[idx],
                                        label: e.target.value,
                                      } as StatItem;
                                      patchLanding({ stats: next });
                                    }}
                                    size="small"
                                    fullWidth
                                    placeholder={t('settings.landing.statsLabelPlaceholder')}
                                  />
                                  <Tooltip title={t('settings.landing.statsRemoveTooltip')}>
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ minWidth: 36, px: 0.5, flexShrink: 0 }}
                                      onClick={() => {
                                        const next = (
                                          landing.stats ?? DEFAULT_LANDING.stats
                                        ).filter((_, i) => i !== idx);
                                        patchLanding({ stats: next });
                                      }}
                                    >
                                      <DeleteRounded fontSize="small" />
                                    </Button>
                                  </Tooltip>
                                </Stack>
                              </Paper>
                            ))}

                            {(landing.stats ?? DEFAULT_LANDING.stats).length < 8 && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<span style={{ fontSize: 18, lineHeight: 1 }}>+</span>}
                                sx={{ alignSelf: 'flex-start' }}
                                onClick={() => {
                                  const next = [
                                    ...(landing.stats ?? DEFAULT_LANDING.stats),
                                    { value: 0, suffix: '', label: '' },
                                  ];
                                  patchLanding({ stats: next });
                                }}
                              >
                                {t('settings.landing.statsAdd')}
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Paper>

                  {/* CTAs & sections */}
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      display="block"
                      mb={2}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                    >
                      {t('settings.landing.sectionsTitle')}
                    </Typography>
                    <Stack spacing={2}>
                      {/* Contact email */}
                      <TextField
                        label={t('settings.landing.contactEmailLabel')}
                        placeholder={t('settings.landing.contactEmailPlaceholder')}
                        type="email"
                        value={landing.contactEmail ?? ''}
                        onChange={(e) => patchLanding({ contactEmail: e.target.value })}
                        fullWidth
                        size="small"
                        helperText={t('settings.landing.contactEmailHelper')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PublicRounded fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Divider />
                      <Stack spacing={0.5}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={landing.showPrograms}
                              onChange={(e) => patchLanding({ showPrograms: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Stack>
                              <Typography variant="body2" fontWeight={600}>
                                {t('settings.landing.showProgramsLabel')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('settings.landing.showProgramsDesc')}
                              </Typography>
                            </Stack>
                          }
                          sx={{ alignItems: 'flex-start', py: 1 }}
                        />
                        <Divider />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={landing.ctaAdmissions}
                              onChange={(e) => patchLanding({ ctaAdmissions: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Stack>
                              <Typography variant="body2" fontWeight={600}>
                                {t('settings.landing.ctaAdmissionsLabel')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('settings.landing.ctaAdmissionsDesc')}
                              </Typography>
                            </Stack>
                          }
                          sx={{ alignItems: 'flex-start', py: 1 }}
                        />
                        <Divider />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={landing.ctaPortal}
                              onChange={(e) => patchLanding({ ctaPortal: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Stack>
                              <Typography variant="body2" fontWeight={600}>
                                {t('settings.landing.ctaPortalLabel')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('settings.landing.ctaPortalDesc')}
                              </Typography>
                            </Stack>
                          }
                          sx={{ alignItems: 'flex-start', py: 1 }}
                        />
                        <Divider />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={landing.ctaFaculty}
                              onChange={(e) => patchLanding({ ctaFaculty: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Stack>
                              <Typography variant="body2" fontWeight={600}>
                                {t('settings.landing.ctaFacultyLabel')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('settings.landing.ctaFacultyDesc')}
                              </Typography>
                            </Stack>
                          }
                          sx={{ alignItems: 'flex-start', py: 1 }}
                        />
                      </Stack>
                    </Stack>
                  </Paper>

                  {/* Contacto del pie */}
                  <ContactEditor
                    contact={landing.contact ?? {}}
                    onChange={(contact) => patchLanding({ contact })}
                  />
                </Stack>

                <Divider sx={{ my: 4 }} />

                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                      {t('settings.domain.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('settings.domain.desc_prefix')}
                      <Box
                        component="code"
                        sx={{
                          bgcolor: 'action.hover',
                          px: 0.8,
                          py: 0.2,
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontSize: 12,
                        }}
                      >
                        dosfilos-domain-proxy.dosfilos.workers.dev
                      </Box>
                    </Typography>
                  </Box>

                  {/* DNS Instructions */}
                  {/* Cloudflare auto-config status */}
                  {cfStatus !== 'idle' && (
                    <Alert
                      severity={
                        cfStatus === 'configured'
                          ? 'success'
                          : cfStatus === 'dns_migration_required'
                            ? 'warning'
                            : cfStatus === 'error'
                              ? 'error'
                              : 'info'
                      }
                      icon={
                        cfStatus === 'configuring' ? (
                          <CircularProgress size={18} />
                        ) : cfStatus === 'configured' ? (
                          <AutoFixHighRounded />
                        ) : (
                          <WarningAmberRounded />
                        )
                      }
                      sx={{ borderRadius: 2 }}
                    >
                      {cfStatus === 'configuring' && t('settings.domain.cfConfiguring')}
                      {cfStatus === 'configured' && t('settings.domain.cfConfigured')}
                      {cfStatus === 'dns_migration_required' && (
                        <>
                          <strong>{t('settings.domain.cfMigrationLabel')}</strong>{' '}
                          {cfMessage ?? t('settings.domain.cfMigrationDefault')}
                        </>
                      )}
                      {cfStatus === 'error' && t('settings.domain.cfError', { message: cfMessage })}
                    </Alert>
                  )}

                  <Alert
                    severity="info"
                    icon={<DnsRounded />}
                    sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
                  >
                    <Typography variant="body2" fontWeight={700} mb={1}>
                      {t('settings.domain.dnsInstructionsTitle')}
                    </Typography>
                    <Typography variant="caption" display="block" mb={1}>
                      {t('settings.domain.dnsInstructionsBody')}
                    </Typography>
                    {customDomains.landing && (
                      <Stack spacing={0.5}>
                        <Box
                          component="code"
                          sx={{
                            display: 'block',
                            bgcolor: 'rgba(0,0,0,0.15)',
                            px: 1.5,
                            py: 0.8,
                            borderRadius: 1,
                            fontSize: 12,
                            fontFamily: 'monospace',
                          }}
                        >
                          Nameservers: rudy.ns.cloudflare.com / tia.ns.cloudflare.com
                        </Box>
                      </Stack>
                    )}
                  </Alert>

                  {/* Landing Domain */}
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <PublicRounded fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={700}>
                        {t('settings.domain.landingTitle')}
                      </Typography>
                      {domainVerifyResult.landing !== undefined && (
                        <Chip
                          size="small"
                          icon={<VerifiedRounded />}
                          label={
                            domainVerifyResult.landing
                              ? t('settings.domain.dnsVerified')
                              : t('settings.domain.dnsPending')
                          }
                          color={domainVerifyResult.landing ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    <Stack spacing={1.5}>
                      <TextField
                        label={t('settings.domain.landingFieldLabel')}
                        placeholder="sebex.cl"
                        value={customDomains.landing ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.toLowerCase().trim();
                          setCustomDomains((prev) => {
                            const { landing: _l, ...rest } = prev;
                            return val ? { ...rest, landing: val } : rest;
                          });
                        }}
                        fullWidth
                        size="small"
                        helperText={t('settings.domain.landingFieldHelper', {
                          slug: slug || t('settings.landing.slugPlaceholder'),
                        })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography variant="caption" color="text.secondary">
                                https://
                              </Typography>
                            </InputAdornment>
                          ),
                          endAdornment: customDomains.landing ? (
                            <InputAdornment position="end">
                              <Tooltip title={t('settings.domain.openInNewTab')}>
                                <Box
                                  component="a"
                                  href={`https://${customDomains.landing}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    display: 'flex',
                                    color: 'text.secondary',
                                    '&:hover': { color: 'primary.main' },
                                  }}
                                >
                                  <OpenInNewRounded fontSize="small" />
                                </Box>
                              </Tooltip>
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Stack>
                  </Paper>

                  {/* Backoffice Domain */}
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <TuneRounded fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={700}>
                        {t('settings.domain.backofficeTitle')}
                      </Typography>
                      {domainVerifyResult.backoffice !== undefined && (
                        <Chip
                          size="small"
                          icon={<VerifiedRounded />}
                          label={
                            domainVerifyResult.backoffice
                              ? t('settings.domain.dnsVerified')
                              : t('settings.domain.dnsPending')
                          }
                          color={domainVerifyResult.backoffice ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    <Stack spacing={1.5}>
                      <TextField
                        label={t('settings.domain.backofficeFieldLabel')}
                        placeholder="selfservice.sebex.cl"
                        value={customDomains.backoffice ?? ''}
                        onChange={(e) => {
                          const val = e.target.value.toLowerCase().trim();
                          setCustomDomains((prev) => {
                            const { backoffice: _b, ...rest } = prev;
                            return val ? { ...rest, backoffice: val } : rest;
                          });
                        }}
                        fullWidth
                        size="small"
                        helperText={t('settings.domain.backofficeFieldHelper')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography variant="caption" color="text.secondary">
                                https://
                              </Typography>
                            </InputAdornment>
                          ),
                          endAdornment: customDomains.backoffice ? (
                            <InputAdornment position="end">
                              <Tooltip title={t('settings.domain.openInNewTab')}>
                                <Box
                                  component="a"
                                  href={`https://${customDomains.backoffice}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    display: 'flex',
                                    color: 'text.secondary',
                                    '&:hover': { color: 'primary.main' },
                                  }}
                                >
                                  <OpenInNewRounded fontSize="small" />
                                </Box>
                              </Tooltip>
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Stack>
                  </Paper>

                  {/* Verify DNS Button */}
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        domainVerifying ? (
                          <CircularProgress size={14} />
                        ) : (
                          <CheckCircleRounded fontSize="small" />
                        )
                      }
                      onClick={handleVerifyDns}
                      disabled={
                        domainVerifying || (!customDomains.landing && !customDomains.backoffice)
                      }
                    >
                      {domainVerifying
                        ? t('settings.domain.verifying')
                        : t('settings.domain.verify')}
                    </Button>
                  </Stack>
                </Stack>
              </TabPanel>

              {/* ── Académico: escala de notas, vigencia de catálogo y rúbricas ── */}
              <TabPanel value={tab} index="academic">
                <Stack spacing={3} sx={{ maxWidth: 1000 }}>
                  {/* ── ESCALA DE CALIFICACIÓN ──────────────────────────────
                      Se guarda con su propio botón, no con el general de la
                      pantalla: cambia cómo se califica TODO el centro y, si se
                      confirma, reexpresa la nota de aprobación de cada
                      plantilla de curso. Mezclarlo con «guardar el logo» sería
                      esconder un acto grande dentro de uno pequeño. */}
                  {currentTenantId && <GradingScaleEditor tenantId={currentTenantId} />}
                  {/* El formato del código de alumno (H6): también con su propio
                      botón, y solo afecta a los códigos nuevos. */}
                  {currentTenantId && <StudentCodeEditor tenantId={currentTenantId} />}
                  {/* M2·H11: cuándo un alumno cuenta como activo en Candidatos
                      (solo en seminarios con expediente por credenciales). */}
                  {currentTenantId && <CandidateActivityEditor tenantId={currentTenantId} />}
                  {/* Candidatos: qué es «cerca» de graduarse (antes, un 4 fijo). */}
                  {currentTenantId && <CandidateNearEditor tenantId={currentTenantId} />}
                  {/* Opt-in on purpose: capping catalog rights is a policy each
                      institution decides, not a default anybody inherits. */}
                  <TextField
                    label={t('settings.framework.catalogRightsExpiryLabel')}
                    type="number"
                    value={catalogRightsExpiryYears}
                    onChange={(e) =>
                      setCatalogRightsExpiryYears(
                        e.target.value === '' ? '' : Number(e.target.value),
                      )
                    }
                    size="small"
                    inputProps={{ min: 0, max: 50 }}
                    helperText={
                      catalogRightsExpiryYears === '' || catalogRightsExpiryYears === 0
                        ? t('settings.framework.catalogRightsExpiryOff')
                        : t('settings.framework.catalogRightsExpiryHelper', {
                            count: Number(catalogRightsExpiryYears),
                          })
                    }
                    sx={{ maxWidth: 320 }}
                  />
                </Stack>
                <Divider sx={{ my: 4 }} />

                <Stack spacing={3} sx={{ maxWidth: 1100 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                      {t('settings.rubrics.catalogTitle')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('settings.rubrics.catalogDesc')}
                    </Typography>
                  </Box>
                  <RubricCatalogEditor
                    value={tenantRubrics}
                    onChange={setTenantRubrics}
                    helperText={t('settings.rubrics.editorHelper')}
                  />
                </Stack>
              </TabPanel>

              {/* ── Marco institucional: misión, versículo, doctrina y TMAI ── */}
              <TabPanel value={tab} index="framework">
                <Stack spacing={3} sx={{ maxWidth: 1000 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                      {t('settings.framework.missionTitle')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('settings.framework.missionDesc')}
                    </Typography>
                  </Box>
                  <TextField
                    label={t('settings.framework.scriptureLabel')}
                    value={missionScripture}
                    onChange={(e) => setMissionScripture(e.target.value)}
                    placeholder={t('settings.framework.scripturePlaceholder')}
                    size="small"
                    helperText={t('settings.framework.scriptureHelper')}
                  />
                  <TextField
                    label={t('settings.framework.missionStatementLabel')}
                    value={missionText}
                    onChange={(e) => setMissionText(e.target.value)}
                    placeholder={t('settings.framework.missionStatementPlaceholder')}
                    multiline
                    minRows={5}
                    fullWidth
                  />
                  <Alert severity="info" icon={<InfoOutlined fontSize="small" />}>
                    {t('settings.framework.programPurposeNote')}
                  </Alert>
                  <TextField
                    label={t('settings.framework.tmaiSiteAbbreviationLabel')}
                    value={tmaiSiteAbbreviation}
                    onChange={(e) => setTmaiSiteAbbreviation(e.target.value)}
                    placeholder="HSEI"
                    size="small"
                    helperText={t('settings.framework.tmaiSiteAbbreviationHelper')}
                    sx={{ maxWidth: 320 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {t('settings.framework.doctrinalStatementLabel')}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      {t('settings.framework.doctrinalStatementHelper')}
                    </Typography>
                    <PageEditor
                      value={doctrinalStatement}
                      onChange={setDoctrinalStatement}
                      placeholder={t('settings.framework.doctrinalStatementPlaceholder')}
                    />
                  </Box>
                </Stack>
              </TabPanel>

              {/* ── Notificaciones ─────────────────────────────────── */}
              <TabPanel value={tab} index="notifications">
                <NotificationsSettingsPage />
              </TabPanel>

              {/* ── Módulos ────────────────────────────────────────── */}
              <TabPanel value={tab} index="modules">
                <Box sx={{ maxWidth: 760 }}>
                  {currentTenantId && <TenantModulesPanel tenantId={currentTenantId} />}
                </Box>
              </TabPanel>
            </>
          )}
        </CardContent>
      </Card>

      {/* Save button */}
      <Stack direction="row" justifyContent="flex-end" mt={3} spacing={2}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRounded />}
          onClick={handleSave}
          disabled={saving || !isValid}
          sx={{
            px: 4,
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`,
            '&:hover': { filter: 'brightness(0.9)' },
            '&:disabled': { opacity: 0.6 },
          }}
        >
          {saving ? t('settings.actions.saving') : t('settings.actions.save')}
        </Button>
      </Stack>

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast?.severity ?? 'success'}
          onClose={() => setToast(null)}
          variant="filled"
          sx={{ minWidth: 300 }}
        >
          {toast?.msg ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
}
